import { useState, useCallback, type ReactNode } from 'react';
import { Outlet, useLocation, useOutletContext } from 'react-router-dom';
import { ReportsTabs } from './ReportsTabs';

// Context type for child routes to inject content into the tabs row
interface ReportsLayoutContext {
  setTabsRightSlot: (node: ReactNode) => void;
}

export const useReportsLayoutContext = () => useOutletContext<ReportsLayoutContext>();

const ReportsLayout = () => {
  const location = useLocation();
  const isReportBuilder = location.pathname.includes('/report-builder');
  const [tabsRightSlot, setTabsRightSlotState] = useState<ReactNode>(null);

  const setTabsRightSlot = useCallback((node: ReactNode) => {
    setTabsRightSlotState(node);
  }, []);

  const getHelpText = () => {
    const path = location.pathname;

    if (path.startsWith('/analytics/reports/view')) {
      return 'View generated report details and download the report data.';
    }

    switch (path) {
      case '/analytics/reports/perform-spark':
        return 'View on-demand reports for performance management and analytics.';
      case '/analytics/reports/configure':
        return 'Create and manage custom report configurations. View live data, run history, and configure scheduled delivery with email notifications.';
      default:
        return 'Manage your reports and analytics.';
    }
  };

  return (
    <div className="flex h-full bg-white">
      <main className="flex-1 overflow-hidden flex flex-col">
        {!isReportBuilder && (
          <div className="bg-white border-b border-gray-200 flex-shrink-0 pt-4">
            <div className="px-6 pb-0">
              <ReportsTabs rightSlot={tabsRightSlot} />
            </div>
            <div className="px-6 pt-3 pb-4">
              <p className="text-sm text-gray-500">{getHelpText()}</p>
            </div>
          </div>
        )}
        <div className="flex-1 overflow-hidden">
          <Outlet context={{ setTabsRightSlot }} />
        </div>
      </main>
    </div>
  );
};

export default ReportsLayout;
